/**
 * Reconnect backoff.
 *
 * ## Why full jitter, and why the floor is not optional
 *
 * The naive schedule - `base * 2 ** attempt` - is a synchronisation primitive in
 * reverse. A Durable Object restart or a Worker deploy drops every socket on the
 * board at the same instant, and without jitter every one of those clients
 * retries at the same instant too, then again two seconds later, then again four
 * seconds later. The retry storm is perfectly correlated with the outage that
 * caused it, which is the classic thundering-herd failure.
 *
 * Full jitter (`random() * ceiling`) spreads the retries uniformly across the
 * whole window and is the variant with the best measured behaviour, but it
 * admits zero: a client that draws 0 retries immediately, and a client unlucky
 * enough to draw 0 twice in a row spins. `MIN_DELAY_MS` removes that case at the
 * cost of a few milliseconds of latency.
 *
 * ## Why `attempt` is clamped
 *
 * `2 ** 1024` is `Infinity`, and `Math.min(cap, Infinity)` is the cap - so the
 * arithmetic is safe. The clamp is here because `attempt` is fed by a counter
 * that increments on every failed connect, and a client that has been offline
 * for a day would otherwise carry a meaningless exponent around. Clamping makes
 * the intent ("the ceiling is reached and stays there") explicit rather than
 * emergent.
 *
 * The function takes its randomness as a parameter rather than calling
 * `Math.random` directly. That is what makes the distribution testable without
 * stubbing a global, and it is the only reason the test file can assert anything
 * stronger than "the result is a number".
 */

/** Starting point for the exponential ceiling. Half a second is short enough
 * that a transient blip is invisible and long enough that a genuine restart is
 * not hammered. */
export const BASE_DELAY_MS = 500;

/** Ceiling. Beyond thirty seconds a client that has recovered stays broken for
 * longer than the outage lasted. */
export const MAX_DELAY_MS = 30_000;

/** Floor, so that a jitter draw of zero cannot produce a hot retry loop. */
export const MIN_DELAY_MS = 250;

/** Beyond this the exponent is noise; the ceiling has long since been reached. */
const MAX_ATTEMPT_EXPONENT = 20;

/**
 * The delay before reconnect attempt `attempt`, where `attempt` is zero-based.
 *
 * @param attempt Number of consecutive failed attempts so far.
 * @param random  Uniform source on `[0, 1)`. Injected so the distribution is
 *                testable; defaults to `Math.random`.
 */
export function backoffDelay(attempt: number, random: () => number = Math.random): number {
  const exponent = Math.min(Math.max(0, Math.floor(attempt)), MAX_ATTEMPT_EXPONENT);
  const ceiling = Math.min(MAX_DELAY_MS, BASE_DELAY_MS * 2 ** exponent);
  return Math.max(MIN_DELAY_MS, Math.round(random() * ceiling));
}

/**
 * Whether a close code should be retried.
 *
 * The classification itself lives in `@slate/shared` (`TERMINAL_CLOSE_CODES`) so
 * that the client and the Durable Object agree on what the codes mean. This
 * wrapper exists so the call site reads as a question rather than as a set
 * lookup, and so the "no code at all" case - which a browser reports as `1006`
 * for a connection that died without a close frame - is handled in one place.
 * `1006` is never terminal: it means the network failed, which is the single
 * most retryable condition there is.
 */
export function isRetryableClose(code: number, terminalCodes: ReadonlySet<number>): boolean {
  if (code === 1006) return true;
  return !terminalCodes.has(code);
}
